import { AlpineMagics } from "@js/shared/alpine";
import { SAFETY_CHECK_VALIDATION_ERROR_MESSAGES } from "@js/shared/error/error";
import { uploadSafetyCheckImage } from "@js/shared/safetyCheckImageUpload";

export type SafetyImage = { key: string; src: string; orientation?: 'portrait' | 'landscape' | null };
export type SafetyCheckValidationField = keyof Pick<
  typeof SAFETY_CHECK_VALIDATION_ERROR_MESSAGES,
  'reportStatus' | 'reportCategories' | 'reportDetail' | 'externalContactStatus' | 'externalContactCase' | 'images'
>;
export type SafetyCheckValidationErrors = Partial<Record<SafetyCheckValidationField, string>>;
export type SafetyCheckValues = {
  reportStatus?: string;
  reportCategories?: string[];
  reportDetail?: string;
  externalContactStatus?: string;
  externalContactCase?: string;
  images?: SafetyImage[];
};
export type SafetyCheckSnapshot = {
  reportStatus: string;
  reportCategories: string[];
  reportDetail: string;
  externalContactStatus: string;
  externalContactCase: string;
  images: SafetyImage[];
  uploading: boolean;
  safetyCheckComplete: boolean;
  validationErrors: SafetyCheckValidationErrors;
};
export const emptySafetyCheckSnapshot = (): SafetyCheckSnapshot => ({
  reportStatus: '',
  reportCategories: [],
  reportDetail: '',
  externalContactStatus: '',
  externalContactCase: '',
  images: [],
  uploading: false,
  safetyCheckComplete: false,
  validationErrors: {}
});

export default {
  reportStatus: '',
  reportCategories: [] as string[],
  reportDetail: '',
  externalContactStatus: '',
  externalContactCase: '',
  images: [] as SafetyImage[],
  uploading: false,

  getAlpineMagic(): AlpineMagics<unknown> {
    return this as unknown as AlpineMagics<unknown>;
  },

  initSafetyCheck(values: SafetyCheckValues = {}) {
    this.reportStatus = values.reportStatus || '';
    this.reportCategories = values.reportCategories || [];
    this.reportDetail = (values.reportDetail || '').trim();
    this.externalContactStatus = values.externalContactStatus || '';
    this.externalContactCase = values.externalContactCase || '';
    this.images = (values.images || []).map((image) => ({ ...image, orientation: image.orientation || null }));
    this.uploading = false;
  },

  safetyCheckComplete(): boolean {
    const reportComplete = this.reportStatus === 'none' ||
      (this.reportStatus === 'exists' && this.reportCategories.length > 0 && this.reportDetail.trim() !== '');
    const contactComplete = this.externalContactStatus === 'none' ||
      this.externalContactStatus === 'proposed_only' ||
      (this.externalContactStatus === 'exchanged' && this.externalContactCase !== '' && this.images.length > 0);
    return reportComplete && contactComplete;
  },

  validationErrors(): SafetyCheckValidationErrors {
    const errors: SafetyCheckValidationErrors = {};
    if (this.reportStatus === '') {
      errors.reportStatus = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.reportStatus;
    }
    if (this.reportStatus === 'exists') {
      if (this.reportCategories.length === 0) {
        errors.reportCategories = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.reportCategories;
      }
      if (this.reportDetail.trim() === '') {
        errors.reportDetail = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.reportDetail;
      }
    }
    if (this.externalContactStatus === '') {
      errors.externalContactStatus = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.externalContactStatus;
    }
    if (this.externalContactStatus === 'exchanged') {
      if (this.externalContactCase === '') {
        errors.externalContactCase = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.externalContactCase;
      }
      if (this.images.length === 0) {
        errors.images = SAFETY_CHECK_VALIDATION_ERROR_MESSAGES.images;
      }
    }
    return errors;
  },

  safetyCheckSnapshot(): SafetyCheckSnapshot {
    return {
      reportStatus: this.reportStatus,
      reportCategories: [...this.reportCategories],
      reportDetail: this.reportDetail,
      externalContactStatus: this.externalContactStatus,
      externalContactCase: this.externalContactCase,
      images: [...this.images],
      uploading: this.uploading,
      safetyCheckComplete: this.safetyCheckComplete(),
      validationErrors: this.validationErrors()
    };
  },

  notifySafetyCheckChange() {
    this.getAlpineMagic().$el.dispatchEvent(
      new CustomEvent('safety-check-changed', { bubbles: true, detail: this.safetyCheckSnapshot() })
    );
  },

  normalizeReportDetail() {
    this.reportDetail = this.reportDetail.trim();
  },

  async uploadSafetyImage(event: Event, replaceIndex?: number) {
    const replacedImage = replaceIndex == null ? undefined : this.images[replaceIndex];
    await uploadSafetyCheckImage(
      this,
      event,
      replaceIndex,
      (image) => this.scrollToSafetyImage(image.key),
      (file) => URL.createObjectURL(file)
    );
    if (replacedImage && this.images[replaceIndex!]?.src !== replacedImage.src) {
      this.revokeSafetyImagePreview(replacedImage);
    }
  },

  async scrollToSafetyImage(key: string) {
    await this.getAlpineMagic().$nextTick();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    const image = Array.from(
      this.getAlpineMagic().$refs.safetyImages.querySelectorAll<HTMLElement>('[data-safety-image-key]')
    ).find((element) => element.dataset.safetyImageKey === key);
    image?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
  },

  removeSafetyImage(index: number) {
    const image = this.images[index];
    if (!image) {
      return;
    }

    this.revokeSafetyImagePreview(image);
    this.images.splice(index, 1);
  },

  revokeSafetyImagePreview(image: SafetyImage) {
    if (image.src.startsWith('blob:')) {
      URL.revokeObjectURL(image.src);
    }
  },

  setSafetyImageOrientation(image: SafetyImage, event: Event) {
    const element = event.currentTarget as HTMLImageElement;
    image.orientation = element.naturalHeight > element.naturalWidth ? 'portrait' : 'landscape';
    this.notifySafetyCheckChange();
  },

  destroy() {
    this.images.forEach((image) => this.revokeSafetyImagePreview(image));
  },

  init() {
    const dataset = this.getAlpineMagic().$el.dataset;
    this.initSafetyCheck(JSON.parse(dataset.initialSafetyCheck || '{}') as SafetyCheckValues);
  }
};
